/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api'

import {
  BuildWithSteps,
  Pipeline,
  PipelineList,
  ProjectSlug,
  Workflow,
  WorkflowJob,
  WorkflowJobList,
  WorkflowList,
} from './types'

/** @public */
export const circleCIApiRef = createApiRef<CircleCIApi>({
  id: 'plugin.circleci.service',
})

const DEFAULT_PROXY_PATH = '/circleci'

// Catalog annotations still use the long `github`/`bitbucket` names; v2 wants
// the short forms.
const VCS_SLUG_MAP: Record<string, string> = {
  gh: 'gh',
  github: 'gh',
  bb: 'bb',
  bitbucket: 'bb',
  circleci: 'circleci',
}

/** @public */
export const toProjectSlug = ({ vcs, owner, repo }: ProjectSlug): string => {
  const vendor = VCS_SLUG_MAP[vcs.toLowerCase()] ?? vcs
  return `${vendor}/${owner}/${repo}`
}

/** @public */
export class CircleCIApi {
  private readonly discoveryApi: DiscoveryApi
  private readonly fetchApi: FetchApi
  private readonly proxyPath: string

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi; proxyPath?: string }) {
    this.discoveryApi = options.discoveryApi
    this.fetchApi = options.fetchApi
    this.proxyPath = options.proxyPath ?? DEFAULT_PROXY_PATH
  }

  async getPipelines(
    project: ProjectSlug,
    options: { pageToken?: string; branch?: string } = {}
  ): Promise<PipelineList> {
    const search = new URLSearchParams()
    if (options.pageToken) search.set('page-token', options.pageToken)
    if (options.branch) search.set('branch', options.branch)

    const qs = search.toString()
    const path = `project/${toProjectSlug(project)}/pipeline${qs ? `?${qs}` : ''}`
    return this.getV2<PipelineList>(path)
  }

  async getPipeline(pipelineId: string): Promise<Pipeline> {
    return this.getV2<Pipeline>(`pipeline/${pipelineId}`)
  }

  async getWorkflowsForPipeline(pipelineId: string): Promise<WorkflowList> {
    return this.getV2<WorkflowList>(`pipeline/${pipelineId}/workflow`)
  }

  async getJobsForWorkflow(workflowId: string): Promise<WorkflowJobList> {
    return this.getV2<WorkflowJobList>(`workflow/${workflowId}/job`)
  }

  async getWorkflow(workflowId: string): Promise<Workflow> {
    return this.getV2<Workflow>(`workflow/${workflowId}`)
  }

  async getJob(project: ProjectSlug, jobNumber: number): Promise<WorkflowJob> {
    return this.getV2<WorkflowJob>(`project/${toProjectSlug(project)}/job/${jobNumber}`)
  }

  // v2 doesn't expose step-level output URLs, so the build-with-steps page
  // still needs v1.1.
  async getBuild(project: ProjectSlug, buildNumber: number): Promise<BuildWithSteps> {
    const vendor = VCS_SLUG_MAP[project.vcs.toLowerCase()] ?? project.vcs
    // v1.1 wants the long vendor name.
    const v1Vendor = vendor === 'gh' ? 'github' : vendor === 'bb' ? 'bitbucket' : vendor

    const url = `${await this.getV1Url()}/project/${v1Vendor}/${project.owner}/${project.repo}/${buildNumber}`
    const response = await this.fetchApi.fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch build: ${response.status} ${response.statusText}`)
    }
    return response.json()
  }

  // The step `output_url` is a CircleCI presigned URL; we route it through
  // the proxy to dodge CORS.
  async getBuildStepActionOutput(url: string): Promise<{ message: string }[]> {
    const circleciPresignedUrlPrefix = 'https://circleci.com/api/private/output/presigned/'
    if (!url.startsWith(circleciPresignedUrlPrefix)) {
      return [{ message: 'Invalid URL' }]
    }

    const proxyUrl = `${await this.discoveryApi.getBaseUrl('proxy')}${this.proxyPath}/presigned/`
    const response = await this.fetchApi.fetch(url.replace(circleciPresignedUrlPrefix, proxyUrl))

    if (!response.ok) {
      return [{ message: `Failed to fetch action output: ${response.statusText}. Look at the details in CircleCI` }]
    }

    return response.json()
  }

  private async getV2<T>(path: string): Promise<T> {
    const url = `${await this.getV2Url()}/${path}`
    const response = await this.fetchApi.fetch(url)
    if (!response.ok) {
      throw new Error(`CircleCI request failed: ${response.status} ${response.statusText}`)
    }
    return response.json() as Promise<T>
  }

  private async getV2Url(): Promise<string> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy')
    return `${proxyUrl}${this.proxyPath}/v2`
  }

  private async getV1Url(): Promise<string> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy')
    return `${proxyUrl}${this.proxyPath}/v1.1`
  }
}
